import React, { useState, useEffect, useContext, useRef } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import CircularProgress from "@material-ui/core/CircularProgress";
import Box from "@material-ui/core/Box";
import Typography from "@material-ui/core/Typography";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";

import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { FormControl, FormControlLabel, Grid, IconButton, Switch } from "@material-ui/core";
import Autocomplete, { createFilterOptions } from "@material-ui/lab/Autocomplete";
import moment from "moment";
import { AuthContext } from "../../context/Auth/AuthContext";
import { isArray } from "lodash";
import DeleteOutline from "@material-ui/icons/DeleteOutline";
import AttachFile from "@material-ui/icons/AttachFile";
import { head } from "lodash";
import ConfirmationModal from "../ConfirmationModal";
import MessageVariablesPicker from "../MessageVariablesPicker";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
  },
  multFieldLine: {
    display: "flex",
    "& > *:not(:last-child)": {
      marginRight: theme.spacing(1),
    },
  },
  btnWrapper: {
    position: "relative",
  },
  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
  sentBanner: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    backgroundColor: theme.palette.type === "dark" ? "#0d2a4a" : "#e3f2fd",
    color: theme.palette.type === "dark" ? "#90caf9" : "#1565c0",
    padding: theme.spacing(1, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  errorBanner: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    backgroundColor: theme.palette.type === "dark" ? "#3e1010" : "#ffebee",
    color: theme.palette.type === "dark" ? "#ef9a9a" : "#c62828",
    padding: theme.spacing(1, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
}));

const ScheduleSchema = Yup.object().shape({
  body: Yup.string().min(5, "Mensagem muito curta").required("Obrigatório"),
  contactId: Yup.number().required("Obrigatório"),
  sendAt: Yup.string().required("Obrigatório"),
});

const filterOptions = createFilterOptions({ limit: 25 });

const ScheduleModal = ({
  open,
  onClose,
  scheduleId,
  contactId,
  cleanContact,
  reload,
  selectedDate,
}) => {
  const classes = useStyles();
  const history = useHistory();
  const { user } = useContext(AuthContext);

  const emptySchedule = () => ({
    body: "",
    contactId: "",
    sendAt: selectedDate
      ? moment(selectedDate).format("YYYY-MM-DDTHH:mm")
      : moment().add(1, "hour").format("YYYY-MM-DDTHH:mm"),
    sentAt: "",
    status: "",
  });

  const [schedule, setSchedule] = useState(emptySchedule());
  const [currentContact, setCurrentContact] = useState({ id: "", name: "" });
  const [contacts, setContacts] = useState([{ id: "", name: "" }]);
  const [queues, setQueues] = useState([]);
  const [keepTicketOpen, setKeepTicketOpen] = useState(true);
  const [scheduleQueueId, setScheduleQueueId] = useState("");
  const [attachment, setAttachment] = useState(null);
  const attachmentFile = useRef(null);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const messageInputRef = useRef();

  const isReadOnly = !!schedule.sentAt && schedule.sentAt !== "";
  const isError = schedule.status === "ERRO";

  const getDialogTitle = () => {
    if (!scheduleId) return "Novo Agendamento";
    if (isError) return "Erro de Envio";
    if (isReadOnly) return "Agendamento Enviado";
    return "Editar Agendamento";
  };

  useEffect(() => {
    if (!open) return;

    const defaultSendAt = selectedDate
      ? moment(selectedDate).format("YYYY-MM-DDTHH:mm")
      : moment().add(1, "hour").format("YYYY-MM-DDTHH:mm");

    setSchedule({ ...emptySchedule(), sendAt: defaultSendAt });
    setCurrentContact({ id: "", name: "" });
    setKeepTicketOpen(true);
    setScheduleQueueId("");

    (async () => {
      try {
        const [{ data: contactList }, { data: queueList }] = await Promise.all([
          api.get("/contacts/list", { params: { companyId: user.companyId } }),
          api.get("/queue"),
        ]);
        const mapped = isArray(contactList)
          ? contactList.map((c) => ({ id: c.id, name: c.name }))
          : [];
        setContacts([{ id: "", name: "" }, ...mapped]);
        setQueues(isArray(queueList) ? queueList : []);

        if (contactId) {
          setSchedule((prev) => ({ ...prev, contactId, sendAt: defaultSendAt }));
        }

        if (!scheduleId) return;

        const { data } = await api.get(`/schedules/${scheduleId}`);
        setSchedule((prev) => ({
          ...prev,
          ...data,
          sendAt: moment(data.sendAt).format("YYYY-MM-DDTHH:mm"),
        }));
        setKeepTicketOpen(data.keepTicketOpen ?? true);
        setScheduleQueueId(data.scheduleQueueId ?? "");
        setCurrentContact(data.contact || { id: "", name: "" });
      } catch (err) {
        toastError(err);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduleId, contactId, open, user, selectedDate]);

  useEffect(() => {
    if (contactId && contacts.length > 1) {
      const contact = contacts.find((c) => c.id === contactId);
      if (contact) setCurrentContact(contact);
    }
  }, [contactId, contacts]);

  const handleClose = () => {
    onClose();
    setAttachment(null);
    setSchedule(emptySchedule());
    setCurrentContact({ id: "", name: "" });
    setQueues([]);
    setKeepTicketOpen(true);
    setScheduleQueueId("");
  };

  const handleAttachmentFile = (e) => {
    const file = head(e.target.files);
    if (file) setAttachment(file);
  };

  const handleSaveSchedule = async (values) => {
    const scheduleData = {
      ...values,
      userId: user.id,
      keepTicketOpen,
      scheduleQueueId: keepTicketOpen ? (scheduleQueueId || null) : null,
    };
    try {
      if (scheduleId) {
        await api.put(`/schedules/${scheduleId}`, scheduleData);
        if (attachment) {
          const formData = new FormData();
          formData.append("file", attachment);
          await api.post(`/schedules/${scheduleId}/media-upload`, formData);
        }
      } else {
        const { data } = await api.post("/schedules", scheduleData);
        if (attachment) {
          const formData = new FormData();
          formData.append("file", attachment);
          await api.post(`/schedules/${data.id}/media-upload`, formData);
        }
      }
      toast.success(i18n.t("scheduleModal.success"));
      if (typeof reload === "function") reload();
      if (contactId && typeof cleanContact === "function") {
        cleanContact();
        history.push("/schedules");
      }
    } catch (err) {
      toastError(err);
    }
    handleClose();
  };

  const handleClickMsgVar = async (msgVar, setValueFunc) => {
    const el = messageInputRef.current;
    const firstHalf = el.value.substring(0, el.selectionStart);
    const secondHalf = el.value.substring(el.selectionEnd);
    const newCursorPos = el.selectionStart + msgVar.length;
    setValueFunc("body", `${firstHalf}${msgVar}${secondHalf}`);
    await new Promise((r) => setTimeout(r, 100));
    messageInputRef.current.setSelectionRange(newCursorPos, newCursorPos);
  };

  const deleteMedia = async () => {
    if (attachment) {
      setAttachment(null);
      attachmentFile.current.value = null;
    }
    if (schedule.mediaPath) {
      await api.delete(`/schedules/${schedule.id}/media-upload`);
      setSchedule((prev) => ({ ...prev, mediaPath: null }));
      toast.success(i18n.t("scheduleModal.toasts.deleted"));
      if (typeof reload === "function") reload();
    }
  };

  return (
    <div className={classes.root}>
      <ConfirmationModal
        title={i18n.t("scheduleModal.confirmationModal.deleteTitle")}
        open={confirmationOpen}
        onClose={() => setConfirmationOpen(false)}
        onConfirm={deleteMedia}
      >
        {i18n.t("scheduleModal.confirmationModal.deleteMessage")}
      </ConfirmationModal>

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        scroll="paper"
      >
        <DialogTitle id="form-dialog-title">{getDialogTitle()}</DialogTitle>

        {isReadOnly && !isError && (
          <Box className={classes.sentBanner}>
            <InfoOutlinedIcon fontSize="small" />
            <Typography variant="body2">
              Enviado em{" "}
              {moment(schedule.sentAt).format("DD/MM/YYYY HH:mm")}. Não é
              possível editar.
            </Typography>
          </Box>
        )}

        {isError && (
          <Box className={classes.errorBanner}>
            <ErrorOutlineIcon fontSize="small" />
            <Typography variant="body2">
              Houve um erro no envio. Ajuste o horário e salve para tentar
              novamente.
            </Typography>
          </Box>
        )}

        <div style={{ display: "none" }}>
          <input
            type="file"
            accept=".png,.jpg,.jpeg,.pdf,.mp3,.mp4,.ogg,.opus,.webp"
            ref={attachmentFile}
            onChange={handleAttachmentFile}
          />
        </div>

        <Formik
          initialValues={schedule}
          enableReinitialize={true}
          validationSchema={ScheduleSchema}
          onSubmit={(values, actions) => {
            setTimeout(() => {
              handleSaveSchedule(values);
              actions.setSubmitting(false);
            }, 400);
          }}
        >
          {({ touched, errors, isSubmitting, setFieldValue }) => (
            <Form>
              <DialogContent dividers>
                {/* Contato */}
                <div className={classes.multFieldLine}>
                  <FormControl variant="outlined" fullWidth>
                    <Autocomplete
                      fullWidth
                      disabled={isReadOnly}
                      value={currentContact}
                      options={contacts}
                      filterOptions={filterOptions}
                      onChange={(e, contact) => {
                        const id = contact ? contact.id : "";
                        setSchedule((prev) => ({ ...prev, contactId: id }));
                        setCurrentContact(contact || { id: "", name: "" });
                        setFieldValue("contactId", id);
                      }}
                      getOptionLabel={(option) => option.name || ""}
                      getOptionSelected={(option, value) => option.id === value.id}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          label={i18n.t("scheduleModal.form.contact")}
                          error={touched.contactId && Boolean(errors.contactId)}
                          helperText={touched.contactId && errors.contactId}
                        />
                      )}
                    />
                  </FormControl>
                </div>

                <Box mt={2} mb={1}>
                  <Field
                    as={TextField}
                    rows={5}
                    multiline
                    label={i18n.t("scheduleModal.form.body")}
                    name="body"
                    inputRef={messageInputRef}
                    error={touched.body && Boolean(errors.body)}
                    helperText={touched.body && errors.body}
                    variant="outlined"
                    fullWidth
                    disabled={isReadOnly}
                  />
                </Box>

                {!isReadOnly && (
                  <Grid item>
                    <MessageVariablesPicker
                      disabled={isSubmitting}
                      onClick={(value) => handleClickMsgVar(value, setFieldValue)}
                    />
                  </Grid>
                )}

                <Box mt={2}>
                  <Field
                    as={TextField}
                    label={i18n.t("scheduleModal.form.sendAt")}
                    type="datetime-local"
                    name="sendAt"
                    InputLabelProps={{ shrink: true }}
                    error={touched.sendAt && Boolean(errors.sendAt)}
                    helperText={touched.sendAt && errors.sendAt}
                    variant="outlined"
                    fullWidth
                    disabled={isReadOnly}
                  />
                </Box>

                {!isReadOnly && (
                  <Box mt={2}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={keepTicketOpen}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setKeepTicketOpen(checked);
                            if (!checked) setScheduleQueueId("");
                          }}
                          color="primary"
                        />
                      }
                      label="Manter ticket aberto para resposta"
                    />
                    {keepTicketOpen && (
                      <FormControl variant="outlined" fullWidth style={{ marginTop: 8 }}>
                        <Autocomplete
                          fullWidth
                          value={queues.find((q) => q.id === scheduleQueueId) || null}
                          options={queues}
                          onChange={(e, queue) => {
                            setScheduleQueueId(queue ? queue.id : "");
                          }}
                          getOptionLabel={(option) => option.name || ""}
                          getOptionSelected={(option, value) => option.id === value.id}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              variant="outlined"
                              label="Fila para receber resposta"
                            />
                          )}
                        />
                      </FormControl>
                    )}
                  </Box>
                )}

                {(schedule.mediaPath || attachment) && (
                  <Grid xs={12} item style={{ marginTop: 8 }}>
                    <Button startIcon={<AttachFile />}>
                      {attachment ? attachment.name : schedule.mediaName}
                    </Button>
                    {!isReadOnly && (
                      <IconButton
                        onClick={() => setConfirmationOpen(true)}
                        color="secondary"
                        size="small"
                      >
                        <DeleteOutline color="secondary" />
                      </IconButton>
                    )}
                  </Grid>
                )}
              </DialogContent>

              <DialogActions>
                {!isReadOnly && !attachment && !schedule.mediaPath && (
                  <Button
                    color="primary"
                    onClick={() => attachmentFile.current.click()}
                    disabled={isSubmitting}
                    variant="outlined"
                  >
                    {i18n.t("quickMessages.buttons.attach")}
                  </Button>
                )}
                <Button
                  onClick={handleClose}
                  color="secondary"
                  disabled={isSubmitting}
                  variant="outlined"
                >
                  {isReadOnly
                    ? "Fechar"
                    : i18n.t("scheduleModal.buttons.cancel")}
                </Button>
                {!isReadOnly && (
                  <Button
                    type="submit"
                    color="primary"
                    disabled={isSubmitting}
                    variant="contained"
                    className={classes.btnWrapper}
                  >
                    {scheduleId
                      ? i18n.t("scheduleModal.buttons.okEdit")
                      : i18n.t("scheduleModal.buttons.okAdd")}
                    {isSubmitting && (
                      <CircularProgress
                        size={24}
                        className={classes.buttonProgress}
                      />
                    )}
                  </Button>
                )}
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </div>
  );
};

export default ScheduleModal;
